import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { library } from '@cwos/api-client';
import {
  Button,
  FilteredEmpty,
  Pill,
  Refused,
  ScopeNote,
  SkeletonSet,
  SuccessEmpty,
  TableCard,
  useTabListKeys,
  type Column,
} from '@cwos/ui-admin';
import { useAuth } from '../../lib/auth.js';
import { useNav } from '../navigation.js';
import { Applicable } from './Applicable.js';
import { ApprovalDecision } from './ApprovalDecision.js';
import { Articles } from './Articles.js';
import { AddToLibrary } from './AddToLibrary.js';
import { usePermissions } from '../permissions.js';
import { authorityGate, gateOpen, type AuthorityGate } from '../authorityGate.js';
import { GateNotice } from '../GateNotice.js';
import { listPhase } from '../listState.js';
import { ListState } from '../ListStates.js';
import { LoadFailure } from '../LoadFailure.js';
import {
  bindingnessLabel,
  bindingnessVariant,
  effectiveWindow,
  extractionKindLabel,
  extractionStateLabel,
  formatDay,
  formatInstantDay,
  ingestionStateLabel,
  pendingTile,
  ruleLevelLabel,
  ruleStateLabel,
  ruleStateVariant,
  scopeCaveat,
} from './libraryLogic.js';

/**
 * THE ORGANIZATIONAL LIBRARY — "what does Phibian currently know about this
 * organization?"
 *
 * ===========================================================================
 * WHAT THIS SCREEN IS FOR
 * ===========================================================================
 * The ingestion pipeline behind it has been merged, mounted and reachable for
 * months: four tables under FORCE row-level security, an approval trigger, an
 * immutability trigger, a fail-closed applicability resolver, twenty-two
 * routes. Nothing called any of them. A customer could not see the Library at
 * all, and the only tenant knowledge that ever reached a model was an article
 * TITLE inside a typed tool result.
 *
 * So this is a door, not an architecture. Every read below goes through
 * `@cwos/api-client`'s `library` module to a route that already existed or that
 * this lane added inside the plugin `server.ts` already registers. No new
 * permission key. No new scope.
 *
 * ===========================================================================
 * NEVER EXPOSE GRAPH INTERNALS JUST BECAUSE THEY EXIST
 * ===========================================================================
 * The waterline doctrine, applied to a knowledge store, which is the surface
 * most tempting to violate it: the data underneath really is a graph of
 * sources, extractions, rules, versions, supersession edges and seven
 * applicability arrays, and every one of those is a plausible column.
 *
 * None of them is rendered raw. `libraryLogic.ts` is a total translation from
 * the storage vocabulary into operator language — `strong_default` becomes
 * "Expected", `client_specific` becomes "Set for one person", `superseded`
 * becomes "Replaced" — and the applicability ARRAYS never reach the screen at
 * all: they hold UUIDs, and `clientIds` holds participant identifiers, so what
 * renders is which DIMENSIONS a rule was narrowed on and never which rows.
 *
 * ===========================================================================
 * THE FOUR STATES, ON EVERY PANEL
 * ===========================================================================
 * `<ListState>` carries loading / refused / failed / empty / stale for the
 * three tables. Two states this screen must get right by hand, because they are
 * not list states:
 *
 *   REFUSED IS NOT ZERO. The review-queue tile reads `pendingApprovalsRefused`
 *   and says so in words. `0` for a caller who may not ask would tell a
 *   supervisor their queue is clear when they simply cannot see it.
 *
 *   NARROWED IS NOT ORGANIZATIONAL. `countsAreOrganizationWide` is false for a
 *   reader scoped to their own programs, and the headline figures then carry a
 *   `<ScopeNote>`. A narrowed total presented as an organizational one is the
 *   "scope applied after the fact" defect wearing a headline number.
 */

/** The reading permission every panel on this screen is gated by, server-side. */
export const LIBRARY_READ_KEY = 'knowledge:read';

/** The permission the review queue is gated by. One key above reading. */
export const LIBRARY_REVIEW_KEY = 'knowledge:publish';

/** The permission adding to the Library needs (`POST /knowledge/sources`, proposing). */
export const LIBRARY_WRITE_KEY = 'knowledge:write';

export const LIBRARY_IS =
  'The Library is everything Phibian has been told about how your organization works: the ' +
  'documents you have given it, the rules a person approved out of those documents, and what ' +
  'is still waiting for someone to decide. Nothing here takes effect because it was uploaded — ' +
  'a rule is in force only once a person approved it, and the Library shows you who and when.';

type Tab = 'overview' | 'articles' | 'sources' | 'rules' | 'applicable' | 'approvals';

/**
 * SIX TABS, ONE DESTINATION.
 *
 * The coverage audit files these as three orphans — O3 knowledge articles, O4
 * doctrine sources and approvals, O15 the Operational Library's catalog and
 * applicability — and says in as many words that they are ONE destination and
 * not three. The founder's doctrine ruling requires the same thing from the
 * other direction: there is one canonical corpus, and there must not be a
 * second independent Intelligence doctrine system beside it.
 *
 * So an operator asking "what does this organization say about supervision"
 * never has to know whether the answer was TYPED into Phibian (Articles),
 * UPLOADED to it (Sources), APPROVED out of an upload (Policies & rules), or
 * WORKED OUT from all of them for a particular situation (What applies).
 */
const TABS: readonly { key: Tab; label: string }[] = [
  { key: 'overview', label: 'Overview' },
  { key: 'articles', label: 'Articles' },
  { key: 'sources', label: 'Sources' },
  { key: 'rules', label: 'Policies & rules' },
  { key: 'applicable', label: 'What applies' },
  { key: 'approvals', label: 'Approvals' },
];

export function Library() {
  const [tab, setTab] = useState<Tab>('overview');
  const [adding, setAdding] = useState(false);
  const libraryTabs = useTabListKeys<HTMLDivElement>();
  const permissions = usePermissions();
  const mayWrite = permissions.has(LIBRARY_WRITE_KEY);
  /**
   * Unknown is not denied. This was `has(LIBRARY_REVIEW_KEY)`, and both
   * consumers below turned the resulting `false` into a sentence about the
   * reader's account — which `has()` also answers while `/auth/me` is in flight
   * and after it failed. The route gate above charges `LIBRARY_READ_KEY`, a
   * DIFFERENT key, so a stale set that opened the screen and a refresh that
   * failed reach here with the review answer genuinely unknown.
   */
  const reviewGate = authorityGate(permissions, LIBRARY_REVIEW_KEY);

  return (
    <div className="rn-col">
      <header className="rn-card">
        <div className="rn-card-head">
          <h1 className="rn-card-title">Library</h1>
          {/* THE ONE DOOR IN, on every tab — upload, write a rule, add guidance. */}
          {mayWrite && !adding ? (
            <Button variant="primary" onClick={() => setAdding(true)}>
              + Add to Library
            </Button>
          ) : null}
        </div>
        <p className="rn-muted">{LIBRARY_IS}</p>
      </header>

      {adding ? <AddToLibrary onClose={() => setAdding(false)} /> : null}

      <div role="tablist" aria-label="Library sections" className="rn-row" {...libraryTabs}>
        {TABS.map((t) => (
          <button
            key={t.key}
            type="button"
            role="tab"
            id={`library-tab-${t.key}`}
            aria-controls="library-panel"
            aria-selected={tab === t.key}
            className="rn-tab-btn"
            onClick={() => setTab(t.key)}
          >
            {t.label}
          </button>
        ))}
      </div>

      <div id="library-panel" role="tabpanel" aria-labelledby={`library-tab-${tab}`}>
        {tab === 'overview' ? (
          <Overview reviewGate={reviewGate} onRetry={permissions.refetch} />
        ) : null}
        {tab === 'articles' ? <Articles /> : null}
        {tab === 'sources' ? <Sources /> : null}
        {tab === 'rules' ? <Rules /> : null}
        {tab === 'applicable' ? <Applicable /> : null}
        {tab === 'approvals' ? (
          <Approvals reviewGate={reviewGate} onRetry={permissions.refetch} />
        ) : null}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Overview
// ---------------------------------------------------------------------------

function Overview({ reviewGate, onRetry }: { reviewGate: AuthorityGate; onRetry: () => void }) {
  const overview = useQuery({
    queryKey: ['library', 'overview'],
    queryFn: () => library.overview(),
  });

  if (overview.isLoading) return <SkeletonSet surface="tiles" rows={3} />;
  if (overview.isError) {
    return (
      <LoadFailure
        error={overview.error}
        message="We couldn’t read your Library just now. This is not a statement that it is empty."
        onRetry={() => void overview.refetch()}
        refusedHeading="The Library is not available on this account"
      />
    );
  }

  const data = overview.data;
  if (!data) return null;
  const pending = pendingTile(data);
  const caveat = scopeCaveat(data);

  return (
    <div className="rn-col">
      {caveat ? <ScopeNote scope={caveat} /> : null}

      <div className="rn-row" role="group" aria-label="What Phibian knows">
        <Figure
          label="Documents"
          value={data.sources}
          detail="Handbooks, policies and notices you have given Phibian."
        />
        <Figure
          label="Rules in force"
          value={data.rulesActive}
          detail={`Answering ${data.subjects === 1 ? '1 question' : `${data.subjects} questions`} about how you operate.`}
        />
        <Figure
          label="Replaced"
          value={data.rulesSuperseded}
          detail="Rules a newer one took over from. Kept, so June’s decision can still be explained."
        />
        <Figure
          label="Not yet classified"
          value={data.sourcesUnclassified}
          detail="Documents nobody has said what kind of thing they are. Not a problem — just undone."
        />

        {/* THE TILE THAT MUST NOT COLLAPSE. Three answers, three sentences. */}
        <div className="rn-card" role="status">
          <p className="rn-muted">Waiting for review</p>
          {pending.kind === 'count' ? (
            <p className="rn-strong">{pending.label}</p>
          ) : (
            <p className="rn-strong">{pending.label}</p>
          )}
          <p className="rn-muted">{pending.detail}</p>
        </div>
      </div>

      <section className="rn-card">
        <h2 className="rn-card-title">How something becomes a rule</h2>
        <p className="rn-muted">
          Add a document or write a rule with “+ Add to Library”. A person then proposes what it
          means, and an approver accepts it and sets the date it starts. Until then nothing you add
          changes what Phibian recommends. Once approved, Ask Phibian can cite it, naming the
          document it came from.
        </p>
        {/* `refused` and not `!allowed`: "approving needs a permission you do
            not hold" is a claim about the account, and the other two answers
            are not entitled to it. */}
        <GateNotice
          gate={reviewGate}
          what="approve what goes into the Library"
          onRetry={onRetry}
          refusedNode={
            <p className="rn-muted">
              You can read the Library. Approving what goes into it needs a separate permission —
              ask your org admin if you need it.
            </p>
          }
        />
      </section>
    </div>
  );
}

function Figure({ label, value, detail }: { label: string; value: number; detail: string }) {
  return (
    <div className="rn-card">
      <p className="rn-muted">{label}</p>
      {/* A MEASURED ZERO IS A REAL ANSWER and is rendered as one. The refusal
          case never reaches this component — it has its own tile — so `0` here
          always means we looked and found none. */}
      <p className="rn-strong">{value}</p>
      <p className="rn-muted">{detail}</p>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Sources
// ---------------------------------------------------------------------------

function Sources() {
  const navigate = useNav();
  const { me } = useAuth();
  const [state, setState] = useState<library.IngestionState | ''>('');
  const [query, setQuery] = useState('');
  const [appliedQuery, setAppliedQuery] = useState('');

  const list = useQuery({
    queryKey: ['library', 'sources', state],
    queryFn: () => library.sources(state === '' ? {} : { ingestionState: state }),
    enabled: appliedQuery === '',
  });

  const search = useQuery({
    queryKey: ['library', 'search', appliedQuery],
    queryFn: () => library.searchSources(appliedQuery, 25),
    enabled: appliedQuery !== '',
  });

  const rows = list.data ?? [];

  const columns: Column<library.LibrarySource>[] = [
    {
      key: 'title',
      header: 'Document',
      render: (s) => (
        <span>
          <span className="rn-strong">{s.title}</span>
          {s.instructionShaped ? (
            <>
              {' '}
              {/* A REVIEWER HINT, NOT A BLOCK. The document is stored,
                  searchable and inert either way; what this says is "somebody
                  should look at this", never "this was refused". */}
              <Pill variant="look">Contains text addressed to a computer</Pill>
            </>
          ) : null}
        </span>
      ),
    },
    {
      key: 'version',
      header: 'Version',
      render: (s) => s.versionLabel ?? <span className="rn-muted">Not versioned</span>,
    },
    { key: 'state', header: 'Status', render: (s) => ingestionStateLabel(s.ingestionState) },
    {
      key: 'classification',
      header: 'Kind',
      // `null` means NOT YET CLASSIFIED, which is a real state and a different
      // one from "classified as nothing". Rendering an empty cell would erase
      // that difference.
      render: (s) => s.classification ?? <span className="rn-muted">Not yet classified</span>,
    },
    {
      key: 'effective',
      header: 'Effective from',
      render: (s) =>
        s.effectiveFrom ? formatDay(s.effectiveFrom) : <span className="rn-muted">Not dated</span>,
    },
    {
      key: 'derived',
      header: 'Rules from it',
      numeric: true,
      render: (s) => s.ruleCount,
    },
    {
      key: 'uploader',
      header: 'Uploaded by',
      render: (s) =>
        s.uploadedByUserId === null ? (
          <span className="rn-muted">Not recorded</span>
        ) : s.uploadedByUserId === me?.userId ? (
          'You'
        ) : (
          <span className="rn-muted">Another admin</span>
        ),
    },
    { key: 'retrieved', header: 'Added', render: (s) => formatInstantDay(s.retrievedAt) },
  ];

  return (
    <div className="rn-col">
      <div className="rn-row kl-toolbar">
        <form
          className="kl-search"
          role="search"
          onSubmit={(e) => {
            e.preventDefault();
            setAppliedQuery(query.trim());
          }}
        >
          <input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search what your documents actually say…"
            aria-label="Search document text"
          />
          <Button type="submit" variant="secondary">
            Search
          </Button>
          {appliedQuery !== '' ? (
            <button
              type="button"
              className="rn-link"
              onClick={() => {
                setQuery('');
                setAppliedQuery('');
              }}
            >
              Clear
            </button>
          ) : null}
        </form>
      </div>

      {appliedQuery !== '' ? (
        <SearchResults query={appliedQuery} search={search} />
      ) : (
        <>
          <label>
            Status
            <select
              value={state}
              onChange={(e) => setState(e.target.value as library.IngestionState | '')}
            >
              <option value="">Every status</option>
              <option value="source_available">Current</option>
              <option value="superseded">Replaced</option>
              <option value="retired">Withdrawn</option>
            </select>
          </label>

          <ListState
            phase={listPhase(list, list.data === undefined ? undefined : rows.length)}
            surface="table"
            rows={6}
            failedMessage="We couldn’t load your documents just now. This is not a statement that you have none."
            onRetry={() => void list.refetch()}
            refused={
              <Refused
                heading="The Library is not available on this account"
                message="Reading your organization’s documents needs a permission you do not hold. It is withheld, not empty."
              />
            }
            empty={
              state === '' ? (
                <SuccessEmpty
                  heading="No documents yet"
                  message="Use “+ Add to Library” to give Phibian a handbook or a policy. Uploading one changes nothing on its own — a person still has to approve what it means."
                />
              ) : (
                <FilteredEmpty onClear={() => setState('')} />
              )
            }
          >
            <TableCard
              columns={columns}
              rows={rows}
              rowKey={(s) => s.id}
              onRowOpen={(s) => navigate(`/library/sources/${s.id}`)}
              caption="Documents this organization has given Phibian"
            />
          </ListState>
        </>
      )}
    </div>
  );
}

/**
 * CONTENT SEARCH — the real `GET /knowledge/sources/search` route, exposed in
 * the admin UI for the first time. Full-text (Postgres FTS), not semantic:
 * there is no embeddings layer in this product, and this screen does not
 * pretend otherwise. Results are `Evidence`, the same untrusted-reference-data
 * shape a turn would receive — so what you see here searching is exactly what
 * Ask Phibian would receive if this same text were quoted to it.
 */
function SearchResults({
  query,
  search,
}: {
  query: string;
  search: ReturnType<typeof useQuery<library.Evidence[], Error>>;
}) {
  const navigate = useNav();
  const rows = search.data ?? [];

  if (search.isLoading) return <SkeletonSet surface="table" rows={4} />;
  if (search.isError) {
    return (
      <LoadFailure
        error={search.error}
        message="We couldn’t search your documents just now."
        onRetry={() => void search.refetch()}
        refusedHeading="Searching the Library is not available on this account"
      />
    );
  }
  if (rows.length === 0) {
    return (
      <SuccessEmpty
        heading="Nothing found"
        message={`No document’s text matches “${query}”. Try different words, or check Sources for the document by name.`}
      />
    );
  }
  return (
    <div className="rn-col">
      <p className="rn-muted">
        {rows.length === 1 ? '1 document matches' : `${rows.length} documents match`} “{query}” —
        this is the same text Ask Phibian would receive if it quoted one of these.
      </p>
      {rows.map((r, i) => (
        <section className="rn-card kl-searchresult" key={`${r.provenance.sourceId}:${i}`}>
          <div className="rn-card-head">
            <button
              type="button"
              className="rn-link rn-card-title"
              onClick={() => navigate(`/library/sources/${r.provenance.sourceId}`)}
            >
              {r.provenance.sourceTitle}
            </button>
            {r.provenance.versionLabel ? (
              <span className="rn-muted">{r.provenance.versionLabel}</span>
            ) : null}
          </div>
          <p className="rn-muted kl-excerpt">{excerptAround(r.text, query)}</p>
        </section>
      ))}
    </div>
  );
}

/** A short, readable window of the matched text — not the whole document. */
function excerptAround(text: string, query: string, radius = 220): string {
  const lower = text.toLowerCase();
  const at = lower.indexOf(query.toLowerCase().split(/\s+/)[0] ?? '');
  if (at === -1) return text.length > radius * 2 ? `${text.slice(0, radius * 2)}…` : text;
  const start = Math.max(0, at - radius);
  const end = Math.min(text.length, at + radius);
  return `${start > 0 ? '…' : ''}${text.slice(start, end)}${end < text.length ? '…' : ''}`;
}

// ---------------------------------------------------------------------------
// Policies & rules
// ---------------------------------------------------------------------------

function Rules() {
  const navigate = useNav();
  const [state, setState] = useState<library.RuleState | ''>('active');

  const list = useQuery({
    queryKey: ['library', 'rules', state],
    queryFn: () => library.rules(state === '' ? {} : { state }),
  });

  const rows = list.data ?? [];

  const columns: Column<library.LibraryRule>[] = [
    {
      key: 'statement',
      header: 'Rule',
      // NEVER WITHHELD. The organization's own policy is a thing its people are
      // entitled to read; only the document's NAME is ever withheld.
      render: (r) => <span className="rn-strong">{r.statement}</span>,
    },
    {
      key: 'binding',
      header: 'How binding',
      render: (r) => (
        <Pill variant={bindingnessVariant(r.bindingness)}>{bindingnessLabel(r.bindingness)}</Pill>
      ),
    },
    { key: 'level', header: 'Applies to', render: (r) => ruleLevelLabel(r.ruleLevel) },
    {
      key: 'window',
      header: 'In effect',
      render: (r) => effectiveWindow(r),
    },
    {
      key: 'state',
      header: 'Status',
      render: (r) => <Pill variant={ruleStateVariant(r.state)}>{ruleStateLabel(r.state)}</Pill>,
    },
    {
      key: 'source',
      header: 'From',
      // WITHHELD IS NOT ABSENT. `sourceTitle` is null exactly when
      // `sourceWithheld` is true — the flag is read FIRST, before the null,
      // because "we have it and you may not see it" and "there is none" are
      // different sentences and the null alone cannot tell them apart.
      render: (r) =>
        r.sourceWithheld ? (
          <span className="rn-muted">Not shown to you — the document is outside your programs</span>
        ) : (
          (r.sourceTitle ?? <span className="rn-muted">Unnamed document</span>)
        ),
    },
  ];

  return (
    <div className="rn-col">
      <label>
        Status
        <select value={state} onChange={(e) => setState(e.target.value as library.RuleState | '')}>
          <option value="active">In force</option>
          <option value="superseded">Replaced</option>
          <option value="retired">Withdrawn</option>
          <option value="">Every status</option>
        </select>
      </label>

      <ListState
        phase={listPhase(list, list.data === undefined ? undefined : rows.length)}
        surface="table"
        rows={6}
        failedMessage="We couldn’t load your rules just now. This is not a statement that you have none."
        onRetry={() => void list.refetch()}
        refused={
          <Refused
            heading="The Library is not available on this account"
            message="Reading your organization’s rules needs a permission you do not hold. It is withheld, not empty."
          />
        }
        empty={
          state === 'active' ? (
            <SuccessEmpty
              heading="Nothing in force yet"
              message="A rule gets here when somebody approves a reading taken from one of your documents. Until then Phibian goes on what it can see in your records."
            />
          ) : (
            <FilteredEmpty onClear={() => setState('')} />
          )
        }
      >
        <TableCard
          columns={columns}
          rows={rows}
          rowKey={(r) => r.id}
          onRowOpen={(r) => navigate(`/library/rules/${r.id}`)}
          caption="What your organization has decided, and when each decision applies"
        />
      </ListState>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Approvals
// ---------------------------------------------------------------------------

function Approvals({ reviewGate, onRetry }: { reviewGate: AuthorityGate; onRetry: () => void }) {
  const mayReview = gateOpen(reviewGate);
  /**
   * THE ONE OPEN, DECIDED IN PLACE.
   *
   * `/library/approvals/$extractionId` is a route this lane cannot add —
   * `App.tsx` has one owner — so the queue holds the open reading in its own
   * state. Nothing is lost by that: a decision is taken from the queue and
   * returns to the queue, which is the shape a reviewer works in anyway.
   */
  const [openId, setOpenId] = useState<string | null>(null);

  const list = useQuery({
    queryKey: ['library', 'approvals'],
    queryFn: () => library.pendingExtractions(),
    // NEVER FIRE A READ YOU KNOW IS REFUSED. The route answers 403 without the
    // reviewing key, and a refusal we can predict is one to explain rather than
    // to provoke.
    enabled: mayReview,
  });

  if (!mayReview) {
    return (
      <GateNotice
        gate={reviewGate}
        what="decide what goes into the Library"
        onRetry={onRetry}
        refusedNode={
          <Refused
            heading="Approvals are not shown to you"
            message="Deciding what goes into the Library needs a separate permission from reading it. Nothing is being hidden by mistake — ask your org admin if you need it."
          />
        }
      />
    );
  }

  const rows = list.data ?? [];
  const open = rows.find((e) => e.id === openId) ?? null;

  if (open !== null) {
    return (
      <ApprovalDecision
        extraction={open}
        onDecided={() => {
          setOpenId(null);
          void list.refetch();
        }}
        onCancel={() => setOpenId(null)}
      />
    );
  }

  const columns: Column<library.Extraction>[] = [
    {
      key: 'statement',
      header: 'Proposed',
      // UNTRUSTED TEXT, rendered as characters. React escapes it; nothing in
      // this feature interprets a proposed statement as markup.
      render: (e) => <span className="rn-strong">{e.proposedStatement}</span>,
    },
    { key: 'kind', header: 'What it is', render: (e) => extractionKindLabel(e.kind) },
    { key: 'subject', header: 'Answers', render: (e) => e.subject },
    {
      key: 'binding',
      header: 'Proposed as',
      render: (e) => (
        <Pill variant={bindingnessVariant(e.proposedBindingness)}>
          {bindingnessLabel(e.proposedBindingness)}
        </Pill>
      ),
    },
    { key: 'state', header: 'Status', render: (e) => extractionStateLabel(e.state) },
    { key: 'proposed', header: 'Proposed', render: (e) => formatInstantDay(e.proposedAt) },
  ];

  return (
    <div className="rn-col">
      <p className="rn-muted">
        Nothing on this list is in force. These are readings somebody proposed from one of your
        documents; each one becomes a rule only when a person approves it, and the approval records
        who and when.
      </p>

      <ListState
        phase={listPhase(list, list.data === undefined ? undefined : rows.length)}
        surface="table"
        rows={5}
        failedMessage="We couldn’t load the review queue just now. This is not a statement that it is empty."
        onRetry={() => void list.refetch()}
        refused={
          <Refused
            heading="Approvals are not shown to you"
            message="Deciding what goes into the Library needs a separate permission from reading it."
          />
        }
        empty={
          <SuccessEmpty
            heading="Nothing waiting"
            message="Every reading taken from your documents has been decided."
          />
        }
      >
        <TableCard
          columns={columns}
          rows={rows}
          rowKey={(e) => e.id}
          onRowOpen={(e) => setOpenId(e.id)}
          caption="Proposed readings awaiting a person’s decision"
        />
      </ListState>
    </div>
  );
}
